import axios from "axios";
import api from "@/app/lib/axios";

import { ChatMessage } from "@/types/chat";

// Shared global casino chat lives on Laravel (Reverb + Sanctum), so these use the
// default `api` instance — unlike crash/roulette which hit the Go games-service.

/** Last ~50 messages, oldest→newest. Public. */
export async function fetchChatHistory(): Promise<ChatMessage[]> {
    const { data } = await api.get<ChatMessage[]>("chat");
    return data;
}

/**
 * Post a message. Auth required; rate-limited server-side. Pass the Echo socket
 * id so the server's ->toOthers() excludes us from the broadcast (we add the
 * message optimistically from this response).
 */
export interface ReplyMeta {
    reply_to_id: number;
    reply_to_username: string;
    reply_to_snippet: string;
}

export async function sendChatMessage(message: string, socketId?: string | null, reply?: ReplyMeta): Promise<ChatMessage> {
    try {
        const { data } = await api.post<ChatMessage>("chat", { message, ...reply }, {
            headers: socketId ? { "X-Socket-Id": socketId } : undefined,
        });
        return data;
    } catch (err) {
        if (axios.isAxiosError(err)) {
            throw new Error(err.response?.data?.message ?? "Failed to send message");
        }
        throw err;
    }
}

/** Remove a message (mod+). */
export async function deleteChatMessage(id: number): Promise<void> {
    await api.delete(`admin/chat/${id}`);
}

/** Delete all messages from a user (mod+). */
export async function purgeUserMessages(userId: number): Promise<{ purged: number }> {
    const { data } = await api.delete<{ purged: number }>(`admin/user/${userId}/messages`);
    return data;
}

/** Mute a user for `minutes` minutes, or unmute if already muted (mod+). */
export async function muteUser(userId: number, minutes = 10): Promise<{ success: boolean; message: string; user: { muted_until: string | null } }> {
    const { data } = await api.post(`admin/user/${userId}/mute`, { minutes });
    return data;
}
